/**
 * documentLibrary.service.ts — Admin > Documents: the account's document
 * library (categories, documents, their files) and the packets built from it,
 * plus the e-signature fields of each and the placement editor's preview.
 *
 * Calls /v5/document-library, the corporate dashboard's document library API
 * (a port of the legacy Documents section, html/lib/admin/admin-documents.php
 * and its actions/admin-documents-save.php) for this account's own staff. The
 * account is the one the request is made for; Admin Access is required.
 *
 * Shapes and call names follow corp-dashboard's lib/document-library-types.ts
 * and the document library actions in lib/api-actions.tsx, minus the account
 * argument, so the placement editor ported from corp reads the same. Every
 * call answers a LibraryResult: the data, or the server's message as written.
 * Labels are the legacy section's.
 */
import { apiClient, getErrorMessage } from './apiClient';
import { presignAndPutFile } from './uploads.service';

/** Result of a document library call: the data, or a message to show as written. */
export type LibraryResult<T> =
  | { ok: true; data: T }
  | { ok: false; message: string; code?: string; field?: string; status?: number };

/** documents.format / document_packets.format: the signature layout, in the legacy section's words. */
export const SIGNATURE_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'No signature' },
  { value: 'patient', label: 'Patient signature' },
  { value: 'patientpri', label: 'Patient signature and printed name' },
  { value: 'patientwit', label: 'Patient and witness signatures' },
  { value: 'patientpwi', label: 'Patient signature, printed name and witness signature' },
  { value: 'patientpgu', label: 'Patient signature, printed name and parent or guardian signature' },
];

export function signatureTypeLabel(value: string): string {
  return SIGNATURE_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? value;
}

export type FieldType = 'signature' | 'initials' | 'date' | 'name' | 'shorttext' | 'longtext';

/** document_keywords.keywordType */
export const FIELD_TYPE_OPTIONS: ReadonlyArray<{ value: FieldType; label: string }> = [
  { value: 'signature', label: 'Signature' },
  { value: 'initials', label: 'Initials' },
  { value: 'date', label: "Today's Date" },
  { value: 'name', label: 'Printed Name' },
  { value: 'shorttext', label: 'Short Text' },
  { value: 'longtext', label: 'Long Text' },
];

export function fieldTypeLabel(value: string): string {
  return FIELD_TYPE_OPTIONS.find((o) => o.value === value)?.label ?? (value === '' ? '(not set)' : `${value} (not a known value)`);
}

export type PacketRequirement = 'plan' | 'doc' | 'coord' | 'fac';

/** document_packets.requires: "Only send when the patient has". */
export const PACKET_REQUIRES_OPTIONS: ReadonlyArray<{ value: PacketRequirement; label: string }> = [
  { value: 'plan', label: 'A plan' },
  { value: 'doc', label: 'A doctor' },
  { value: 'coord', label: 'A coordinator' },
  { value: 'fac', label: 'A facility' },
];

/** document_packets.keywordSource: where a packet's e-signature fields come from. */
export const KEYWORD_SOURCE_OPTIONS: ReadonlyArray<{ value: 0 | 1; label: string }> = [
  { value: 0, label: "This packet's own fields" },
  { value: 1, label: 'The documents inside it' },
];

export const DOCUMENT_UPLOAD_EXTENSIONS = ['doc', 'docx', 'xlsx', 'pdf', 'odt', 'rtf', 'label'] as const;
export const DOCUMENT_UPLOAD_MAX_BYTES = 20 * 1024 * 1024;
export const PROCEDURE_SLOT_MAX = 300;
/** The server's limits (DocumentLibrary controllers). */
export const CATEGORY_NAME_MAX = 50;
export const DOCUMENT_TITLE_MAX = 150;
export const DOCUMENT_DESCRIPTION_MAX = 100;
export const PACKET_NAME_MAX = 150;

export interface LibraryDocument {
  documentID: number;
  categoryID: number;
  title: string;
  description: string;
  fileName: string;
  format: string;
  emailContentID: number;
  smsID: number;
  active: boolean;
  dOrder: number;
  /** Left out of update and restore answers. */
  packetCount?: number;
}

export interface LibraryCategory {
  documentCategoryID: number;
  name: string;
  active: boolean;
  dcOrder: number;
  /** Left out of update and restore answers. */
  documents?: LibraryDocument[];
}

export interface DocumentLibrary {
  accountID: number;
  categories: LibraryCategory[];
}

export interface DocumentPayload {
  title?: string;
  description?: string;
  format?: string;
  categoryID?: number;
  fileName?: string;
}

export interface DocumentUsage {
  documentID: number;
  packets: Array<{ documentPacketID: number; name: string; active: boolean }>;
}

export interface PacketEntry {
  documentPacketEntryID: number;
  documentPacketID: number;
  documentID: number;
  /** 0 = a specific document, 1 = a procedure slot */
  documentType: 0 | 1;
  packetWcId: number;
  orderID: number;
}

export interface DocumentPacket {
  documentPacketID: number;
  name: string;
  description: string;
  format: string;
  requires: PacketRequirement[];
  emailContentID: number;
  smsID: number;
  /** 0 = the packet's own fields, 1 = the fields of the documents inside it */
  keywordSource: 0 | 1;
  active: boolean;
  dpOrder: number;
  entries: PacketEntry[];
}

export interface PacketPayload {
  name?: string;
  description?: string;
  format?: string;
  requires?: PacketRequirement[];
  keywordSource?: 0 | 1;
}

export type FieldOwnerType = 'document' | 'packet';

/** One e-signature field (document_keywords row). X/Y are PDF points, width is mm. */
export interface EsignField {
  documentKeywordID: number;
  title: string;
  tabKeywords: string;
  keywordType: string;
  signatureX: string;
  signatureY: string;
  signaturePageNum: string;
  width: number;
  optional: boolean;
  unplaceable: boolean;
}

/** A field as the editor saves it; a missing or 0 id is a new field. */
export interface EsignFieldInput {
  documentKeywordID?: number;
  title: string;
  tabKeywords: string;
  keywordType: string;
  signatureX: string;
  signatureY: string;
  signaturePageNum: string;
  width: string;
  optional: boolean;
}

export interface EsignFieldSet {
  ownerType: FieldOwnerType;
  ownerID: number;
  keywords: EsignField[];
  deleted?: number;
}

export interface PreviewPage {
  page: number;
  widthMm: number;
  heightMm: number;
  pageScaleFactor: number;
  scaledPageHeightMm: number;
}

/** "pending": a packet member not prepared yet; ask for it by its documentID. */
export type PreviewReason = 'nofile' | 'unsupported' | 'convert' | 'unreadable' | 'unavailable' | 'pending';

export interface PreviewDoc {
  index: number;
  documentID: number | null;
  title: string;
  fileName: string;
  previewable: boolean;
  reason: PreviewReason | null;
  key: string | null;
  url: string | null;
  pages: PreviewPage[];
}

export interface TextRun {
  xPt: number;
  yPt: number;
  text: string;
}

export interface AnchorHit {
  docIndex: number;
  page: number;
  xPt: number;
  yPt: number;
  text: string;
}

export type KeywordWarning = 'empty' | 'shortcode' | 'whitespace';

export interface AnchorsResult {
  anchors: Record<string, AnchorHit[]>;
  warnings: Record<string, KeywordWarning[]>;
}

// ---------------------------------------------------------------------------

const BASE = '/document-library';

function failure(error: unknown): { ok: false; message: string; code?: string; field?: string; status?: number } {
  const response = (error as { response?: { status?: number; data?: { error?: { code?: string; details?: { field?: string } } } } })?.response;
  return {
    ok: false,
    message: getErrorMessage(error, 'That change could not be saved. Check your connection and try again.'),
    code: response?.data?.error?.code,
    field: response?.data?.error?.details?.field,
    status: response?.status,
  };
}

async function call<T>(method: 'get' | 'post' | 'patch' | 'put' | 'delete', path: string, body?: unknown): Promise<LibraryResult<T>> {
  try {
    const res =
      method === 'get' || method === 'delete'
        ? await apiClient[method](`${BASE}${path}`)
        : await apiClient[method](`${BASE}${path}`, body ?? {});
    return { ok: true, data: (res.data?.data ?? null) as T };
  } catch (error) {
    return failure(error);
  }
}

function id(value: number): number {
  if (!Number.isInteger(value) || value <= 0) throw new Error('Invalid id');
  return value;
}

export function getDocumentLibrary(): Promise<LibraryResult<DocumentLibrary>> {
  return call('get', '/library');
}

export function createLibraryCategory(name: string): Promise<LibraryResult<LibraryCategory>> {
  return call('post', '/categories', { name });
}

export function renameLibraryCategory(categoryId: number, name: string): Promise<LibraryResult<LibraryCategory>> {
  return call('patch', `/categories/${id(categoryId)}`, { name });
}

export function setLibraryCategoryActive(categoryId: number, active: boolean): Promise<LibraryResult<LibraryCategory>> {
  return active ? call('post', `/categories/${id(categoryId)}/restore`) : call('delete', `/categories/${id(categoryId)}`);
}

export function createLibraryDocument(payload: DocumentPayload): Promise<LibraryResult<LibraryDocument>> {
  return call('post', '/documents', payload);
}

export function updateLibraryDocument(documentId: number, payload: DocumentPayload): Promise<LibraryResult<LibraryDocument>> {
  return call('patch', `/documents/${id(documentId)}`, payload);
}

export function setLibraryDocumentActive(documentId: number, active: boolean): Promise<LibraryResult<LibraryDocument>> {
  return active ? call('post', `/documents/${id(documentId)}/restore`) : call('delete', `/documents/${id(documentId)}`);
}

/** Save the order of documents; with categoryId, every listed document is filed there too. */
export function reorderLibraryDocuments(ids: number[], categoryId?: number): Promise<LibraryResult<{ ordered: number; categoryID: number | null }>> {
  return call('post', '/documents/reorder', { ids, ...(categoryId ? { categoryID: categoryId } : {}) });
}

export function getLibraryDocumentUsage(documentId: number): Promise<LibraryResult<DocumentUsage>> {
  return call('get', `/documents/${id(documentId)}/usage`);
}

export function getLibraryDocumentFileUrl(documentId: number): Promise<LibraryResult<{ url: string; fileName: string }>> {
  return call('get', `/documents/${id(documentId)}/file`);
}

/**
 * Upload a document's file: straight to storage, then into the account's
 * document folder. Answers the stored name, which the document keeps only once
 * the form is saved with it.
 */
export async function uploadLibraryDocumentFile(file: File): Promise<LibraryResult<{ fileName: string; size: number }>> {
  let s3Key: string;
  try {
    s3Key = await presignAndPutFile(file);
  } catch (error) {
    return failure(error);
  }
  return call('post', '/files', { s3Key, fileName: file.name });
}

export function getEsignFields(ownerType: FieldOwnerType, ownerId: number): Promise<LibraryResult<EsignFieldSet>> {
  const query = new URLSearchParams({ ownerType, ownerID: String(id(ownerId)) });
  return call('get', `/fields?${query.toString()}`);
}

export function saveEsignFields(
  ownerType: FieldOwnerType,
  ownerId: number,
  keywords: EsignFieldInput[],
  deleted: number[],
  /** Documents only: the file the editor is positioning (for example one uploaded but not saved yet). */
  fileName?: string,
): Promise<LibraryResult<EsignFieldSet>> {
  return call('put', '/fields', { ownerType, ownerID: id(ownerId), keywords, deleted, ...(fileName ? { fileName } : {}) });
}

/**
 * Preview info for a document or packet. For a packet the server prepares one
 * member per request (`documentId`, else the first); the others come back
 * "pending" until asked for. Each returned url is short-lived.
 */
export function getEsignPreviewInfo(
  ownerType: FieldOwnerType,
  ownerId: number,
  fileName?: string,
  documentId?: number,
): Promise<LibraryResult<{ docs: PreviewDoc[] }>> {
  return call('post', '/esign/preview-info', {
    ownerType,
    ownerID: id(ownerId),
    ...(fileName ? { fileName } : {}),
    ...(documentId ? { documentID: id(documentId) } : {}),
  });
}

export function getEsignAnchors(previewKeys: string[], keys: string[], texts: string[]): Promise<LibraryResult<AnchorsResult>> {
  return call('post', '/esign/anchors', { previewKeys, keys, texts });
}

export function getEsignTextRuns(previewKey: string): Promise<LibraryResult<{ pages: Record<string, TextRun[]> }>> {
  return call('post', '/esign/text-runs', { previewKey, page: 'all' });
}

export function getDocumentPackets(): Promise<LibraryResult<{ accountID: number; packets: DocumentPacket[] }>> {
  return call('get', '/packets');
}

export function createDocumentPacket(payload: PacketPayload): Promise<LibraryResult<DocumentPacket>> {
  return call('post', '/packets', payload);
}

export function updateDocumentPacket(packetId: number, payload: PacketPayload): Promise<LibraryResult<DocumentPacket>> {
  return call('patch', `/packets/${id(packetId)}`, payload);
}

export function setDocumentPacketActive(packetId: number, active: boolean): Promise<LibraryResult<DocumentPacket>> {
  return active ? call('post', `/packets/${id(packetId)}/restore`) : call('delete', `/packets/${id(packetId)}`);
}

/** Add an item to the end of a packet: a specific document, or a procedure slot (1–300). */
export function addPacketEntry(
  packetId: number,
  item: { documentType: 0; documentID: number } | { documentType: 1; packetWcId: number },
): Promise<LibraryResult<PacketEntry>> {
  return call('post', `/packets/${id(packetId)}/entries`, item);
}

/** Save the order of a packet's items; with packetId, every listed item moves into that packet. */
export function reorderPacketEntries(ids: number[], packetId?: number): Promise<LibraryResult<{ ordered: number; documentPacketID: number | null }>> {
  return call('post', '/packet-entries/reorder', { ids, ...(packetId ? { documentPacketID: packetId } : {}) });
}

/** Take an item out of its packet. The document itself is untouched. */
export function removePacketEntry(entryId: number): Promise<LibraryResult<{ documentPacketEntryID: number }>> {
  return call('delete', `/packet-entries/${id(entryId)}`);
}
